/* «QUESTA ESATTA CONFIGURAZIONE, SU UN PERIODO CHE SCELGO IO, COSA AVREBBE FATTO?»
 *
 * Sta sotto il dettaglio del tradebot perché è lì che la domanda nasce: si
 * guarda un bot che opera con denaro vero e si deve decidere se lasciarlo
 * acceso. Uscire dalla pagina, aprire il modulo del backtest libero e ricopiare
 * a mano quattordici campi è il modo migliore per sbagliarne uno — e un campo
 * sbagliato in un backtest non dà errore, dà un altro risultato.
 *
 * Le regole di questa schermata, in ordine di importanza:
 *
 *  1. LA PRECOMPILAZIONE È ESATTA O DICHIARATA. Ogni campo porta con sé da dove
 *     viene (`provaBacktest.ts`), e tutto ciò che non è letto dalla riga del bot
 *     compare in un elenco a schermo prima del pulsante. Niente valori
 *     plausibili messi in silenzio.
 *  2. I PARAMETRI SI COPIANO, NON SI RICOSTRUISCONO. Stessa macchina di
 *     `creazione.ts`: si parte da `{ ...origine }` e si sovrascrivono solo le
 *     chiavi davvero toccate, nel tipo di partenza. Un `"14"` al posto di `14` è
 *     un'altra strategia con lo stesso nome.
 *  3. SI SEGNALA, NON SI BLOCCA. Cambiare un parametro rispetto al bot è
 *     legittimo — è metà del motivo per cui si prova. Lo scostamento si vede
 *     grande e prima del lancio; il pulsante resta premibile.
 *
 * L'unica cosa che blocca è ciò che renderebbe il risultato non riferibile a
 * questo bot: parametri assenti, exchange non deducibile, un difetto nella copia.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Avvertenza,
  Campo,
  Distintivo,
  GruppoCampi,
  Occhiello,
  Pulsante,
  Scheda,
  Selettore,
  Verdetto,
} from '../../componenti';
import { backtest as apiBacktest, datiStorici, exchanges, tradebots, usaRichiesta } from '../../api';
import type { AvvioBacktest, MarketWatcher, StatoDati, Tradebot } from '../../api';
import { durataPeriodo, num, periodo as etichettaPeriodo, timeframe } from '../../lib/formato';
import { giorniDiWarmup, warmupRichiesto } from '../../lib/warmup';
import { RisultatoBacktest, TernaDelRisultato } from '../backtest/RisultatoBacktest';
import type { CostiEsecuzione } from '../backtest/riesecuzione';
import { VENUE } from '../backtest/costi';
import {
  bozzaIniziale,
  copiaParametri,
  elencoParametri,
  modificabile,
  scostamentiRicetta,
  type Bozza,
  type Ricetta,
  type Scostamento,
} from './creazione';
import {
  COSTI_A_ZERO,
  capitaleAvvioDelBot,
  coperturaCandele,
  costiDaVenue,
  giorno,
  mercatoDelBot,
  mercatoScelto,
  motivoNonRieseguibile,
  periodoProposto,
  sceltaAMano,
  sceltaDaMarketWatcher,
  venueDelBot,
  type Certezza,
  type Dichiarato,
  type SceltaMercato,
} from './provaBacktest';
import type { Correlazione } from './correlazione';

/** Ogni quanto richiedere la riga mentre il motore calcola. Lo stesso passo del
 *  modulo del backtest libero: il calcolo dura minuti, non secondi. */
const MS_POLLING = 5000;

/** Le finestre rapide, oltre a quella del bot. In mesi. */
const FINESTRE: { chiave: string; etichetta: string; mesi: number | null }[] = [
  { chiave: '1a', etichetta: '1 anno', mesi: 12 },
  { chiave: '2a', etichetta: '2 anni', mesi: 24 },
  { chiave: '3a', etichetta: '3 anni', mesi: 36 },
  { chiave: 'tutto', etichetta: 'Tutto lo storico', mesi: null },
];

function numeroDa(testo: string): number | null {
  const t = testo.trim();
  if (!t) return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function menoMesi(mesi: number, da: Date): Date {
  const d = new Date(da.getTime());
  d.setUTCMonth(d.getUTCMonth() - mesi);
  return d;
}

/* ========================================================================== */

export function ProvaConfigurazione({
  bot,
  correlazione,
  statoCorrelazione,
}: {
  bot: Tradebot;
  /** Il backtest che già giustifica questo bot, se la ricerca l'ha trovato. */
  correlazione: Correlazione | undefined;
  statoCorrelazione: StatoDati;
}) {
  const naviga = useNavigate();
  const bloccante = motivoNonRieseguibile(bot);

  /* --- ciò che si deduce dalla riga, con la sua provenienza --- */
  const dedotto = useMemo(() => mercatoDelBot(bot), [bot]);
  const sede = useMemo(() => venueDelBot(bot), [bot]);
  const capitale = useMemo(() => capitaleAvvioDelBot(bot), [bot]);

  /* Quando la riga non dice su quali candele il bot decide, la schermata non si
     limita a dichiararlo: offre l'elenco delle sorgenti possibili. Le richieste
     partono SOLO in quel caso — nel caso normale il market watcher c'è e non si
     va a chiedere niente. */
  const daScegliere =
    dedotto.exchangeId.certezza !== 'certo' || dedotto.asset.certezza !== 'certo';

  const elencoMw = usaRichiesta(() => tradebots.marketWatchers(), [], { attiva: daScegliere });
  const elencoExchange = usaRichiesta(() => exchanges.elenco(), [], { attiva: daScegliere });

  /** `''` = niente scelto · `mw:<id>` = da elenco · `mano` = scritto a mano. */
  const [modoMercato, setModoMercato] = useState('');
  const [manExchange, setManExchange] = useState(
    dedotto.exchangeId.valore != null ? String(dedotto.exchangeId.valore) : '',
  );
  const [manAsset, setManAsset] = useState(dedotto.asset.valore ?? bot.asset);
  const [manCurrency, setManCurrency] = useState(dedotto.currency.valore ?? bot.currency);

  const scelta: SceltaMercato | null = useMemo(() => {
    if (!daScegliere) return null;
    if (modoMercato.startsWith('mw:')) {
      const cercato = modoMercato.slice(3);
      const mw = elencoMw.dati?.find((m: MarketWatcher) => String(m.id) === cercato);
      return mw ? sceltaDaMarketWatcher(mw) : null;
    }
    if (modoMercato === 'mano') {
      const ex = Number(manExchange);
      if (!Number.isFinite(ex) || ex <= 0) return null;
      if (!manAsset.trim() || !manCurrency.trim()) return null;
      const nome = elencoExchange.dati?.data.find((e) => e.id === ex)?.name ?? null;
      return sceltaAMano(ex, manAsset, manCurrency, nome);
    }
    return null;
  }, [
    daScegliere,
    modoMercato,
    elencoMw.dati,
    elencoExchange.dati,
    manExchange,
    manAsset,
    manCurrency,
  ]);

  const mercato = useMemo(
    () => (scelta ? mercatoScelto(bot, scelta) : dedotto),
    [scelta, bot, dedotto],
  );

  /* --- il magazzino delle candele: serve a proporre una fine che esiste --- */
  const storico = usaRichiesta((s) => datiStorici.schema(s), [], { eVuoto: () => false });
  const copertura = useMemo(
    () =>
      coperturaCandele(
        storico.dati,
        mercato.exchangeId.valore,
        mercato.asset.valore ?? bot.asset,
        mercato.currency.valore ?? bot.currency,
      ),
    [storico.dati, mercato, bot.asset, bot.currency],
  );

  const proposta = useMemo(() => periodoProposto(bot, copertura), [bot, copertura]);

  /* --- il periodo: l'unico campo che nasce per essere cambiato --- */
  const [dal, setDal] = useState(proposta.dal);
  const [al, setAl] = useState(proposta.al);
  const [toccatoPeriodo, setToccatoPeriodo] = useState(false);

  /* La proposta si assesta quando arriva il magazzino (la fine passa da «oggi»
     all'ultima candela). Finché l'utente non ha toccato le date, seguirla è
     giusto; dopo, sovrascriverle cancellerebbe una scelta fatta. */
  useEffect(() => {
    if (toccatoPeriodo) return;
    setDal(proposta.dal);
    setAl(proposta.al);
  }, [proposta.dal, proposta.al, toccatoPeriodo]);

  /* --- i parametri: copiati per identità, modificabili, e sorvegliati --- */
  /* La firma, non l'oggetto: la riga del bot si rilegge (dopo un avvio, dopo
     un arresto) e ogni rilettura porta un `strategyParams` nuovo di identità ma
     identico di contenuto. Legarsi all'identità cancellerebbe quello che si sta
     scrivendo nel campo ogni volta che la pagina si aggiorna. */
  const firmaParametri = JSON.stringify(bot.strategyParams ?? {});
  const parametri = useMemo(
    () => elencoParametri(bot.strategyParams ?? {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firmaParametri],
  );
  const [bozze, setBozze] = useState<Record<string, Bozza>>(() => {
    const iniziali: Record<string, Bozza> = {};
    for (const p of elencoParametri(bot.strategyParams ?? {})) {
      iniziali[p.chiave] = bozzaIniziale(p);
    }
    return iniziali;
  });
  useEffect(() => {
    const iniziali: Record<string, Bozza> = {};
    for (const p of parametri) iniziali[p.chiave] = bozzaIniziale(p);
    setBozze(iniziali);
  }, [parametri]);

  const [candela, setCandela] = useState(String(bot.candleSize));
  const [warmup, setWarmup] = useState(String(bot.warmupPeriod));
  const [assetIniziale, setAssetIniziale] = useState(String(capitale.assetBalance.valore ?? 0));
  const [valutaIniziale, setValutaIniziale] = useState(
    String(capitale.currencyBalance.valore ?? 1000),
  );

  /* --- i costi: dal venue misurato, o dichiaratamente assenti --- */
  const costiIniziali: CostiEsecuzione = sede.venue.valore
    ? costiDaVenue(sede.venue.valore)
    : COSTI_A_ZERO;
  const [costi, setCosti] = useState<CostiEsecuzione>(costiIniziali);
  const [venueScelto, setVenueScelto] = useState<string>(sede.venue.valore?.id ?? '');
  useEffect(() => {
    setVenueScelto(sede.venue.valore?.id ?? '');
    setCosti(sede.venue.valore ? costiDaVenue(sede.venue.valore) : COSTI_A_ZERO);
  }, [sede.venue.valore]);

  /* Si apre per primo il gruppo che ha qualcosa da risolvere: un controllo
     dentro un accordion chiuso, con l'errore in fondo alla pagina, è un
     controllo che non esiste per chi legge. */
  const [aperto, setAperto] = useState<number | null>(daScegliere ? 2 : 1);

  /* --- la copia dei parametri, e la sua verifica --- */
  const copia = useMemo(
    () => copiaParametri(bot.strategyParams ?? {}, parametri, bozze),
    [bot.strategyParams, parametri, bozze],
  );

  /* --- cosa non corrisponde più al bot --- */
  const ricetta: Ricetta = useMemo(
    () => ({
      strategyName: bot.strategyName,
      strategyParams: (bot.strategyParams ?? {}) as Record<string, unknown>,
      asset: mercato.asset.valore ?? bot.asset,
      currency: mercato.currency.valore ?? bot.currency,
      candleSize: bot.candleSize,
      warmupPeriod: bot.warmupPeriod,
    }),
    [bot, mercato],
  );

  const scostamenti: Scostamento[] = useMemo(() => {
    const base = scostamentiRicetta(
      ricetta,
      {
        asset: ricetta.asset,
        currency: ricetta.currency,
        candleSize: candela,
        warmupPeriod: warmup,
      },
      parametri,
      bozze,
    );
    /* `scostamentiRicetta` tiene il warmup fuori di proposito, perché nel
       percorso backtest→bot ridurlo non stacca il bot dalla misura. Qui il
       percorso è l'opposto: il warmup è uno dei campi che si sta rimisurando, e
       cambiarlo cambia da dove comincia il periodo davvero misurato. */
    const w = numeroDa(warmup);
    if (w != null && w !== bot.warmupPeriod) {
      base.push({
        chiave: 'warmupPeriod',
        campo: 'Warm-up',
        da: `${num(bot.warmupPeriod)} candele`,
        a: `${num(w)} candele`,
        soloNominale: false,
      });
    }
    return base;
  }, [ricetta, candela, warmup, parametri, bozze, bot.warmupPeriod]);

  /* --- warmup: il minimo che questi parametri chiedono --- */
  const richiestaWarmup = useMemo(() => warmupRichiesto(copia.valori), [copia.valori]);
  const candelaNum = numeroDa(candela);
  const warmupNum = numeroDa(warmup);
  const giorniWarmup = giorniDiWarmup(warmupNum ?? 0, candelaNum ?? 0);
  const giorniFinestra =
    dal && al ? (new Date(al).getTime() - new Date(dal).getTime()) / 86400000 : null;
  const quotaMangiata =
    giorniWarmup != null && giorniFinestra != null && giorniFinestra > 0
      ? giorniWarmup / giorniFinestra
      : null;

  /* --- ciò che manca per lanciare --- */
  const errori: string[] = [];
  if (mercato.exchangeId.valore == null) {
    errori.push('l’exchange delle candele — scegli la sorgente nel riquadro 2');
  }
  if (!dal || !al) errori.push('il periodo');
  else if (dal >= al) errori.push('una data di fine dopo quella di inizio');
  if ((candelaNum ?? 0) <= 0) errori.push('la dimensione della candela');
  if (warmupNum == null) errori.push('il warm-up');
  if (numeroDa(valutaIniziale) == null) errori.push('il capitale in valuta');
  if (copia.invalide.length > 0) errori.push(`un valore valido in ${copia.invalide.join(', ')}`);

  /* --- l'esecuzione --- */
  const [idAvviato, setIdAvviato] = useState<number | null>(null);
  const [giro, setGiro] = useState(0);
  const [riusato, setRiusato] = useState(false);
  const [inAvvio, setInAvvio] = useState(false);
  const [erroreAvvio, setErroreAvvio] = useState<string | null>(null);

  const esecuzione = usaRichiesta(
    (s) => apiBacktest.uno(idAvviato as number, s),
    [idAvviato, giro],
    { attiva: idAvviato != null },
  );
  const inCorso = esecuzione.dati?.status === 'RUNNING';
  const ricaricaEsecuzione = esecuzione.ricarica;
  useEffect(() => {
    if (!inCorso) return;
    const t = setInterval(ricaricaEsecuzione, MS_POLLING);
    return () => clearInterval(t);
  }, [inCorso, ricaricaEsecuzione]);

  const avvia = useCallback(
    async (forza: boolean) => {
      if (bloccante || errori.length > 0 || copia.anomalie.length > 0) return;
      if (mercato.exchangeId.valore == null) return;
      setInAvvio(true);
      setErroreAvvio(null);
      try {
        const corpo: AvvioBacktest = {
          exchangeId: mercato.exchangeId.valore,
          asset: (mercato.asset.valore ?? bot.asset).trim(),
          currency: (mercato.currency.valore ?? bot.currency).trim(),
          startDate: dal,
          endDate: al,
          strategyName: bot.strategyName,
          /* L'oggetto di `copiaParametri`: stesse chiavi, stesso ordine, stessi
             tipi, con sovrascritte le sole chiavi toccate. */
          strategyParams: copia.valori,
          warmupPeriod: warmupNum ?? bot.warmupPeriod,
          candleSize: candelaNum ?? bot.candleSize,
          assetBalance: numeroDa(assetIniziale) ?? 0,
          currencyBalance: numeroDa(valutaIniziale) ?? 0,
          feeMode: costi.feeMode,
          makerFee: costi.makerFee,
          takerFee: costi.takerFee,
          slippage: costi.slippage,
          force: forza,
        };
        const riga = await apiBacktest.avvia(corpo);
        setIdAvviato(riga.id);
        setGiro((g) => g + 1);
        setRiusato(riga.status !== 'RUNNING');
      } catch (e) {
        setErroreAvvio(e instanceof Error ? e.message : 'Avvio non riuscito');
      } finally {
        setInAvvio(false);
      }
    },
    [
      bloccante,
      errori.length,
      copia.anomalie.length,
      copia.valori,
      mercato,
      bot,
      dal,
      al,
      warmupNum,
      candelaNum,
      assetIniziale,
      valutaIniziale,
      costi,
    ],
  );

  const gia = statoCorrelazione === 'pieno' && correlazione?.stato === 'trovato' ? correlazione.riga : null;

  /* Il periodo del backtest già in archivio copre quello chiesto? La chiave di
     riconoscimento non guarda le date, quindi il backtest trovato può essere su
     tutt'altra finestra: dirlo è la differenza fra «aprilo» e «rilancialo». */
  const giaCopre = gia != null && gia.startDate.slice(0, 10) <= dal && gia.endDate.slice(0, 10) >= al;

  return (
    <div style={{ padding: 'var(--sb-pad-pagina)', paddingTop: 0 }}>
      <Scheda>
        <div className="sb-riga" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Occhiello>Prova questa configurazione su un altro periodo</Occhiello>
          <span className="sb-nota">
            {bot.strategyName} · {mercato.asset.valore ?? bot.asset}/
            {mercato.currency.valore ?? bot.currency} · {timeframe(bot.candleSize)}
          </span>
        </div>

        <div className="sb-nota" style={{ margin: 'var(--sb-s-3) 0 var(--sb-s-5)' }}>
          I campi qui sotto sono presi dalla riga del bot: sotto ognuno c’è scritto da dove viene.
          Il periodo è libero — è il senso di questo modulo — e ogni scostamento dal bot viene
          segnalato prima del lancio, mai impedito.
        </div>

        {bloccante ? (
          <Avvertenza livello="grave">{bloccante}</Avvertenza>
        ) : (
          <>
            {/* ---------- il backtest che esiste già ---------- */}
            <BacktestGiaInArchivio
              correlazione={correlazione}
              stato={statoCorrelazione}
              copre={giaCopre}
            />

            {/* ---------- 1. periodo ---------- */}
            <GruppoCampi
              numero={1}
              titolo="Periodo da misurare"
              conteggio={etichettaPeriodo(dal, al)}
              aperto={aperto === 1}
              onApri={() => setAperto(aperto === 1 ? null : 1)}
            >
              <div className="sb-nuovo__coppia-campi">
                <Campo
                  etichetta="Dal"
                  type="date"
                  value={dal}
                  onChange={(e) => {
                    setToccatoPeriodo(true);
                    setDal(e.target.value);
                  }}
                />
                <Campo
                  etichetta="Al"
                  type="date"
                  value={al}
                  onChange={(e) => {
                    setToccatoPeriodo(true);
                    setAl(e.target.value);
                  }}
                />
              </div>

              <Pillole
                copertura={copertura}
                proposta={proposta.dal}
                propostaAl={proposta.al}
                onScegli={(d, a) => {
                  setToccatoPeriodo(true);
                  setDal(d);
                  setAl(a);
                }}
                onProposta={() => {
                  setToccatoPeriodo(false);
                  setDal(proposta.dal);
                  setAl(proposta.al);
                }}
              />

              <div className="sb-nota">
                <b>Partenza proposta:</b> {proposta.fonte}
              </div>

              {copertura.prima == null && (
                <div className="sb-nota">
                  Il magazzino delle candele non dice fin dove arriva questa coppia
                  {storico.stato === 'errore'
                    ? ': la lettura non è riuscita.'
                    : storico.stato === 'caricamento'
                      ? ': lettura in corso.'
                      : ': nessun intervallo registrato per questo exchange e questa coppia. Un periodo fuori dai dati non dà errore, dà un risultato più corto.'}
                </div>
              )}
              {copertura.prima != null && (
                <div className="sb-nota">
                  In magazzino: dal {giorno(new Date(copertura.prima))} al{' '}
                  {giorno(new Date(copertura.ultima as string))} ·{' '}
                  {durataPeriodo(copertura.prima, copertura.ultima)}.
                </div>
              )}

              {quotaMangiata != null && quotaMangiata >= 0.2 && (
                <Avvertenza livello={quotaMangiata >= 0.5 ? 'grave' : 'cautela'}>
                  Il warm-up si mangia <b>{num(quotaMangiata * 100, 0)}%</b> di questa finestra:{' '}
                  {num(giorniWarmup ?? 0, 0)} giorni su {num(giorniFinestra ?? 0, 0)}. Restano{' '}
                  {num(Math.max(0, (giorniFinestra ?? 0) - (giorniWarmup ?? 0)), 0)} giorni davvero
                  misurati. Allarga il periodo all’indietro, o il risultato descriverà una manciata
                  di giorni con l’aria di descriverne molti.
                </Avvertenza>
              )}
            </GruppoCampi>

            {/* ---------- 2. configurazione del bot ---------- */}
            <GruppoCampi
              numero={2}
              titolo="Configurazione del bot"
              conteggio={
                scostamenti.length === 0
                  ? 'identica al bot'
                  : `${num(scostamenti.length)} ${scostamenti.length === 1 ? 'scostamento' : 'scostamenti'}`
              }
              aperto={aperto === 2}
              onApri={() => setAperto(aperto === 2 ? null : 2)}
            >
              <ProvenienzaCampo etichetta="Exchange delle candele" dato={mercato.exchangeId} />
              <ProvenienzaCampo etichetta="Coppia misurata" dato={mercato.asset} />

              {mercato.coppiaScambiataDiversa && (
                <Avvertenza livello="nota">{mercato.coppiaScambiataDiversa}</Avvertenza>
              )}

              {/* Il rimedio sta ACCANTO alla dichiarazione, non altrove: sopra
                  c'è scritto che la riga non lo dice, qui c'è con cosa dirlo. */}
              {daScegliere && (
                <SceltaSorgenteCandele
                  modo={modoMercato}
                  onModo={setModoMercato}
                  elenco={elencoMw.dati}
                  statoElenco={elencoMw.stato}
                  onRileggiElenco={elencoMw.ricarica}
                  exchange={manExchange}
                  onExchange={setManExchange}
                  asset={manAsset}
                  onAsset={setManAsset}
                  currency={manCurrency}
                  onCurrency={setManCurrency}
                  vociExchange={(elencoExchange.dati?.data ?? []).map((e) => ({
                    valore: String(e.id),
                    etichetta: `${e.name} (#${e.id})`,
                  }))}
                  risolto={scelta != null}
                />
              )}

              <div className="sb-nuovo__coppia-campi">
                <Campo
                  etichetta="Candela"
                  suggerimento={`bot: ${timeframe(bot.candleSize)}`}
                  type="number"
                  inputMode="numeric"
                  unita="min"
                  value={candela}
                  onChange={(e) => setCandela(e.target.value)}
                />
                <Campo
                  etichetta="Warm-up"
                  suggerimento={`bot: ${num(bot.warmupPeriod)}`}
                  type="number"
                  inputMode="numeric"
                  unita="candele"
                  value={warmup}
                  onChange={(e) => setWarmup(e.target.value)}
                  errore={
                    warmupNum != null &&
                    richiestaWarmup.minimo > 0 &&
                    warmupNum < richiestaWarmup.minimo
                      ? `Questi parametri ne chiedono almeno ${num(richiestaWarmup.minimo)} (${richiestaWarmup.cause[0]?.chiave}): sotto il minimo la strategia resta inerte dentro il periodo misurato, senza che il backtest fallisca.`
                      : null
                  }
                />
              </div>

              <Divisoria titolo="Parametri della strategia" />
              {parametri.length === 0 && (
                <div className="sb-nota">Questa strategia non ha parametri.</div>
              )}
              {parametri.map((p) => {
                const b = bozze[p.chiave];
                if (p.tipo === 'booleano') {
                  return (
                    <label key={p.chiave} className="sb-nuovo__spunta">
                      <input
                        type="checkbox"
                        checked={b === true}
                        onChange={(e) =>
                          setBozze((v) => ({ ...v, [p.chiave]: e.target.checked }))
                        }
                      />
                      <span>
                        {p.etichetta}
                        <span className="sb-campo__suggerimento"> · bot: {p.testo}</span>
                      </span>
                    </label>
                  );
                }
                return (
                  <Campo
                    key={p.chiave}
                    etichetta={p.etichetta}
                    suggerimento={`bot: ${p.testo}`}
                    testuale={p.tipo !== 'numero'}
                    value={typeof b === 'string' ? b : p.testo}
                    disabled={!modificabile(p)}
                    onChange={(e) => setBozze((v) => ({ ...v, [p.chiave]: e.target.value }))}
                  />
                );
              })}

              <Divisoria titolo="Capitale d’avvio della prova" />
              <div className="sb-nuovo__coppia-campi">
                <Campo
                  etichetta={`Asset (${bot.asset})`}
                  type="number"
                  value={assetIniziale}
                  onChange={(e) => setAssetIniziale(e.target.value)}
                />
                <Campo
                  etichetta={`Valuta (${bot.currency})`}
                  type="number"
                  value={valutaIniziale}
                  onChange={(e) => setValutaIniziale(e.target.value)}
                />
              </div>
              <ProvenienzaCampo etichetta="Da dove viene" dato={capitale.currencyBalance} />
            </GruppoCampi>

            {/* ---------- 3. costi ---------- */}
            <GruppoCampi
              numero={3}
              titolo="Costi di esecuzione"
              conteggio={
                sede.venue.valore
                  ? sede.venue.valore.nome
                  : venueScelto
                    ? (VENUE.find((v) => v.id === venueScelto)?.nome ?? 'scelti a mano')
                    : 'non dedotti'
              }
              aperto={aperto === 3}
              onApri={() => setAperto(aperto === 3 ? null : 3)}
            >
              <ProvenienzaCampo etichetta="Sede" dato={sede.venue} />

              {sede.notaTolleranza && (
                <Avvertenza livello="nota">{sede.notaTolleranza}</Avvertenza>
              )}

              <Selettore
                etichetta="Venue con cui misurare"
                suggerimento="costi misurati il 26/08/2026"
                valore={venueScelto}
                voci={[
                  { valore: '', etichetta: '— nessuno scelto: costi a zero' },
                  ...VENUE.map((v) => ({
                    valore: v.id,
                    etichetta: `${v.nome} · ${num(v.bp, 2)} bp per lato`,
                  })),
                ]}
                onCambia={(id) => {
                  setVenueScelto(id);
                  const v = VENUE.find((x) => x.id === id);
                  setCosti(v ? costiDaVenue(v) : COSTI_A_ZERO);
                }}
              />

              <div className="sb-nota">
                In vigore: commissione {costi.feeMode === 'MAKER' ? 'maker' : 'taker'}{' '}
                {num(costi.feeMode === 'MAKER' ? costi.makerFee : costi.takerFee, 3)}% · slippage{' '}
                {num(costi.slippage, 3)}%.
                {sede.venue.certezza !== 'certo' &&
                  ' Nessun costo è stato dedotto dalla riga del bot: quello che vedi è la tua scelta, non la sua sede.'}
              </div>
            </GruppoCampi>

            {/* ---------- ciò che non viene dalla riga ---------- */}
            <Incognite
              campi={[
                {
                  etichetta: 'Exchange delle candele',
                  dato: mercato.exchangeId,
                  rimedio: { testo: 'Scegli la sorgente delle candele', vai: 2 },
                },
                {
                  etichetta: 'Coppia misurata',
                  dato: mercato.asset,
                  rimedio: { testo: 'Scegli la sorgente delle candele', vai: 2 },
                },
                {
                  etichetta: 'Costi di esecuzione',
                  dato: sede.venue,
                  rimedio: { testo: 'Scegli il venue con cui misurare', vai: 3 },
                },
                {
                  etichetta: 'Asset d’avvio',
                  dato: capitale.assetBalance,
                  rimedio: { testo: 'Scrivi il capitale d’avvio', vai: 2 },
                },
                {
                  etichetta: 'Capitale d’avvio',
                  dato: capitale.currencyBalance,
                  rimedio: { testo: 'Scrivi il capitale d’avvio', vai: 2 },
                },
              ]}
              onVai={setAperto}
            />

            {/* ---------- gli scostamenti: si vedono, non fermano ---------- */}
            {scostamenti.length > 0 && (
              <Avvertenza livello="cautela">
                <b>
                  Stai misurando {scostamenti.length === 1 ? 'un parametro diverso' : 'parametri diversi'}{' '}
                  da quelli con cui il bot opera.
                </b>{' '}
                Il risultato descriverà un’altra configurazione, non questo bot. Puoi lanciare
                comunque.
                <div style={{ marginTop: 'var(--sb-s-3)' }}>
                  {scostamenti.map((s) => (
                    <div key={s.chiave} className="tb-transizione">
                      <span className="tb-transizione__da">
                        {s.campo}: {s.da}
                      </span>
                      <span className="tb-transizione__freccia" aria-hidden="true">
                        →
                      </span>
                      <span className="tb-transizione__a tb-transizione__a--cambiato">{s.a}</span>
                      {s.soloNominale && <span className="sb-nota"> (stesso mercato, altro nome)</span>}
                    </div>
                  ))}
                </div>
              </Avvertenza>
            )}

            {sede.venue.certezza === 'certo' &&
              sede.venue.valore != null &&
              venueScelto !== sede.venue.valore.id && (
                <Avvertenza livello="cautela">
                  <b>Stai misurando con i costi di un’altra sede.</b> Il bot opera su{' '}
                  {sede.venue.valore.nome} ({num(sede.venue.valore.bp, 2)} bp per lato); qui i costi
                  sono quelli di{' '}
                  {venueScelto
                    ? (VENUE.find((v) => v.id === venueScelto)?.nome ?? venueScelto)
                    : 'nessuna sede — zero'}
                  . Su una strategia che gira spesso la differenza fra due venue decide da sola se
                  è praticabile. Puoi lanciare comunque.
                </Avvertenza>
              )}

            {copia.anomalie.length > 0 && (
              <Avvertenza livello="grave">
                <b>La copia dei parametri non è fedele</b> e la prova non viene lanciata: partirebbe
                su valori diversi da quelli scritti. Anomalie: {copia.anomalie.join(' · ')}.
              </Avvertenza>
            )}

            {errori.length > 0 && (
              <Avvertenza livello="grave">
                <b>Manca qualcosa per lanciare:</b> {errori.join(' · ')}
              </Avvertenza>
            )}

            {erroreAvvio && <Avvertenza livello="grave">{erroreAvvio}</Avvertenza>}

            <div style={{ marginTop: 'var(--sb-s-5)' }}>
              <Pulsante
                variante="primario"
                largo
                disabled={inAvvio || errori.length > 0 || copia.anomalie.length > 0}
                onClick={() => avvia(false)}
              >
                {inAvvio
                  ? 'Avvio in corso…'
                  : `Misura questa configurazione su ${etichettaPeriodo(dal, al)}`}
              </Pulsante>
              <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
                Il calcolo può durare minuti e crea una riga di backtest a sé: non tocca il bot, che
                continua a operare come adesso.
              </div>
            </div>

            {/* ---------- l'esito ---------- */}
            {idAvviato != null && (
              <div className="sb-nuovo__esito">
                {riusato && (
                  <Avvertenza livello="cautela">
                    Il server aveva già in archivio un backtest identico e ha restituito quello
                    invece di rifare il calcolo: i numeri qui sotto sono di quell’esecuzione, e i
                    costi con cui è stata misurata possono essere diversi da quelli impostati qui.
                  </Avvertenza>
                )}

                {inCorso && (
                  <Avvertenza livello="cautela">
                    Backtest #{idAvviato} in esecuzione: la pagina si aggiorna da sola ogni cinque
                    secondi. Puoi lasciarla aperta.
                  </Avvertenza>
                )}

                {esecuzione.stato === 'errore' && (
                  <Avvertenza livello="grave">
                    Il backtest #{idAvviato} è stato avviato ma non si riesce a rileggerlo:{' '}
                    {esecuzione.errore?.message ?? 'lettura non riuscita'}.
                  </Avvertenza>
                )}

                {esecuzione.dati?.status === 'ERROR' && (
                  <Avvertenza livello="grave">
                    Il backtest #{idAvviato} si è interrotto con un errore
                    {esecuzione.dati.stopNotes ? `: ${esecuzione.dati.stopNotes}` : '.'} Le metriche
                    che eventualmente compaiono non descrivono il periodo intero.
                  </Avvertenza>
                )}

                {esecuzione.dati && (
                  <>
                    <div className="sb-nuovo__esito-testata">
                      <span className="sb-bt-riquadro__titolo">
                        Risultato · backtest #{esecuzione.dati.id}
                      </span>
                      <Pulsante onClick={() => naviga(`/backtest/${esecuzione.dati?.id}`)}>
                        Apri il dettaglio
                      </Pulsante>
                    </div>

                    <div className="sb-nota" style={{ marginBottom: 'var(--sb-s-4)' }}>
                      Misura del BACKTEST su{' '}
                      {etichettaPeriodo(esecuzione.dati.startDate, esecuzione.dati.endDate)}, non
                      del bot: il bot opera da{' '}
                      {bot.measuredSince
                        ? etichettaPeriodo(bot.measuredSince, new Date())
                        : etichettaPeriodo(bot.startDate ?? bot.createdAt, new Date())}
                      {scostamenti.length > 0 && ' e con parametri che hai cambiato'}.
                    </div>

                    <Verdetto
                      profitto={esecuzione.dati.strategyChange}
                      mercato={esecuzione.dati.marketChange}
                      drawdown={esecuzione.dati.drawdownMaxValue}
                      operazioni={esecuzione.dati.tradesNumber}
                      periodo={etichettaPeriodo(
                        esecuzione.dati.startDate,
                        esecuzione.dati.endDate,
                      )}
                    />
                    <TernaDelRisultato
                      dato={esecuzione.dati}
                      periodo={etichettaPeriodo(
                        esecuzione.dati.startDate,
                        esecuzione.dati.endDate,
                      )}
                    />
                    <RisultatoBacktest
                      dato={esecuzione.dati}
                      periodo={etichettaPeriodo(
                        esecuzione.dati.startDate,
                        esecuzione.dati.endDate,
                      )}
                      onRiesegui={() => avvia(true)}
                      inRiesecuzione={inAvvio}
                    />
                  </>
                )}
              </div>
            )}
          </>
        )}
      </Scheda>
    </div>
  );
}

/* ==========================================================================
 * Pezzi minori
 * ======================================================================== */

function Divisoria({ titolo }: { titolo: string }) {
  return (
    <div style={{ marginTop: 'var(--sb-s-5)' }}>
      <Occhiello>{titolo}</Occhiello>
    </div>
  );
}

const TONO_CERTEZZA: Record<Certezza, 'guadagno' | 'accento' | 'cautela' | 'perdita'> = {
  certo: 'guadagno',
  scelto: 'accento',
  ripiego: 'cautela',
  ignoto: 'perdita',
};

const PAROLA_CERTEZZA: Record<Certezza, string> = {
  certo: 'dalla riga del bot',
  scelto: 'scelto da te',
  ripiego: 'ripiego',
  ignoto: 'non deducibile',
};

/** Un campo precompilato con, sotto, da dove viene. Mai un valore muto. */
function ProvenienzaCampo({
  etichetta,
  dato,
}: {
  etichetta: string;
  dato: Dichiarato<unknown>;
}) {
  return (
    <div className="sb-campo">
      <div className="sb-campo__etichetta">
        <span className="sb-campo__nome">{etichetta}</span>
        <Distintivo tono={TONO_CERTEZZA[dato.certezza]}>{PAROLA_CERTEZZA[dato.certezza]}</Distintivo>
      </div>
      <div className="sb-nota">{dato.fonte}</div>
    </div>
  );
}

/**
 * Tutto ciò che il modulo NON ha letto dalla riga, in un posto solo.
 *
 * Sparso fra i gruppi chiusi non si vedrebbe, e un campo indovinato che non si
 * vede è esattamente il modo di arrivare a una conclusione sbagliata su un bot
 * che muove denaro vero.
 */
interface VoceIncognita {
  etichetta: string;
  dato: Dichiarato<unknown>;
  /** Il controllo che risolve questo campo, e in quale riquadro sta. */
  rimedio: { testo: string; vai: number };
}

function Incognite({ campi, onVai }: { campi: VoceIncognita[]; onVai: (gruppo: number) => void }) {
  const fuori = campi.filter((c) => c.dato.certezza !== 'certo');
  if (fuori.length === 0) {
    return (
      <Avvertenza livello="nota">
        Ogni campo precompilato viene dalla riga del bot: la configurazione di partenza è
        esattamente quella con cui opera. Quello che cambi da qui in poi è segnalato qui sotto.
      </Avvertenza>
    );
  }

  /* Un campo `ignoto` impedisce il lancio, e allora l'avvertenza è grave; uno
     `scelto` non impedisce niente, e allora non deve gridare: dice solo che quel
     valore l'ha messo chi guarda e va ricordato leggendo il risultato. */
  const bloccanti = fuori.filter((c) => c.dato.certezza === 'ignoto');
  const soloScelti = fuori.every((c) => c.dato.certezza === 'scelto');

  return (
    <Avvertenza livello={bloccanti.length > 0 ? 'grave' : soloScelti ? 'nota' : 'cautela'}>
      <b>
        {soloScelti
          ? fuori.length === 1
            ? 'Un campo l’hai scelto tu, non viene dalla riga del bot'
            : `${num(fuori.length)} campi li hai scelti tu, non vengono dalla riga del bot`
          : fuori.length === 1
            ? 'Un campo non viene dalla riga del bot'
            : `${num(fuori.length)} campi non vengono dalla riga del bot`}
        .
      </b>{' '}
      Il risultato è riferibile a questo bot solo per quanto lo sono questi valori.
      <div style={{ marginTop: 'var(--sb-s-3)' }}>
        {fuori.map((c) => (
          <div key={c.etichetta} className="tb-voce">
            <span className="tb-voce__chiave">{c.etichetta}</span>
            <span className="tb-voce__valore">{c.dato.fonte}</span>
          </div>
        ))}
      </div>
      {/* Ogni dichiarazione porta con sé il modo di risolverla. Senza questi
          pulsanti la schermata direbbe «sceglilo a mano» senza dire dove, ed è
          esattamente il vicolo cieco che questo riquadro esiste per evitare. */}
      <div className="sb-riga" style={{ gap: 'var(--sb-s-3)', marginTop: 'var(--sb-s-4)', flexWrap: 'wrap' }}>
        {[...new Map(fuori.map((c) => [c.rimedio.testo, c.rimedio])).values()].map((r) => (
          <Pulsante key={r.testo} variante="tenue" onClick={() => onVai(r.vai)}>
            {r.testo}
          </Pulsante>
        ))}
      </div>
    </Avvertenza>
  );
}

/* --------------------------------------------------------------------------
 * IL RIMEDIO: da quali candele misurare, quando la riga non lo dice.
 *
 * L'elenco dei market watcher è l'insieme esatto delle sorgenti possibili: sono
 * le stesse righe da cui un tradebot prende le candele. La scrittura a mano
 * resta sotto perché l'elenco può non arrivare — e una schermata che dipende da
 * una richiesta per NON essere un vicolo cieco è ancora un vicolo cieco, il
 * giorno in cui quella richiesta fallisce.
 * ------------------------------------------------------------------------ */

function SceltaSorgenteCandele({
  modo,
  onModo,
  elenco,
  statoElenco,
  onRileggiElenco,
  exchange,
  onExchange,
  asset,
  onAsset,
  currency,
  onCurrency,
  vociExchange,
  risolto,
}: {
  modo: string;
  onModo: (v: string) => void;
  elenco: MarketWatcher[] | null;
  statoElenco: StatoDati;
  onRileggiElenco: () => void;
  exchange: string;
  onExchange: (v: string) => void;
  asset: string;
  onAsset: (v: string) => void;
  currency: string;
  onCurrency: (v: string) => void;
  vociExchange: { valore: string; etichetta: string }[];
  risolto: boolean;
}) {
  const voci = [
    {
      valore: '',
      etichetta:
        statoElenco === 'caricamento'
          ? '— leggo i market watcher…'
          : '— nessuna sorgente scelta: il lancio resta bloccato',
    },
    ...(elenco ?? []).map((m) => ({
      valore: `mw:${m.id}`,
      etichetta: `#${m.id} · ${m.asset}/${m.currency} · ${m.exchange?.name ?? `exchange #${m.exchangeId}`}${m.isActive ? '' : ' (non attivo)'}`,
    })),
    { valore: 'mano', etichetta: '— scrivi exchange e coppia a mano —' },
  ];

  return (
    <div className="sb-colonna" style={{ gap: 'var(--sb-s-4)' }}>
      <Selettore
        etichetta="Da quali candele misurare"
        suggerimento={
          elenco ? `${num(elenco.length)} market watcher esistenti` : 'elenco dei market watcher'
        }
        valore={modo}
        voci={voci}
        onCambia={onModo}
        errore={risolto ? null : 'Finché non ne scegli una, il backtest non può partire.'}
      />

      {statoElenco === 'errore' && (
        <Avvertenza livello="cautela">
          L’elenco dei market watcher non è arrivato: puoi rileggerlo, oppure scrivere exchange e
          coppia a mano qui sotto.{' '}
          <Pulsante variante="nudo" onClick={onRileggiElenco}>
            Rileggi l’elenco
          </Pulsante>
        </Avvertenza>
      )}

      {statoElenco === 'vuoto' && (
        <div className="sb-nota">
          Non esiste nessun market watcher: la sorgente va scritta a mano qui sotto.
        </div>
      )}

      {modo === 'mano' && (
        <>
          {/* Se nemmeno l'elenco degli exchange arriva, resta il campo numerico:
              l'ultima uscita non deve dipendere da una richiesta, altrimenti è
              un vicolo cieco che aspetta solo il giorno giusto per aprirsi. */}
          {vociExchange.length > 0 ? (
            <Selettore
              etichetta="Exchange delle candele"
              valore={exchange}
              voci={[{ valore: '', etichetta: '— scegli —' }, ...vociExchange]}
              onCambia={onExchange}
              errore={exchange ? null : 'Serve l’exchange da cui vengono le candele.'}
            />
          ) : (
            <Campo
              etichetta="Exchange delle candele"
              suggerimento="l’elenco non è arrivato: scrivi l’id"
              type="number"
              inputMode="numeric"
              value={exchange}
              onChange={(e) => onExchange(e.target.value)}
              errore={
                Number(exchange) > 0
                  ? null
                  : 'Serve l’id dell’exchange da cui vengono le candele (lo trovi in Impostazioni · Exchange).'
              }
            />
          )}
          <div className="sb-nuovo__coppia-campi">
            <Campo
              etichetta="Asset osservato"
              testuale
              value={asset}
              onChange={(e) => onAsset(e.target.value)}
              errore={asset.trim() ? null : 'Obbligatorio.'}
            />
            <Campo
              etichetta="Valuta osservata"
              testuale
              value={currency}
              onChange={(e) => onCurrency(e.target.value)}
              errore={currency.trim() ? null : 'Obbligatorio.'}
            />
          </div>
          <div className="sb-nota">
            È la coppia su cui il bot DECIDE, non quella che scambia: su catena le due possono
            avere nomi diversi (BTCB comprato, BTC/USDT osservato). Sbagliarla non dà errore, dà
            il risultato di un altro mercato.
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Il backtest che già giustifica questo bot.
 *
 * Se esiste, rifare il calcolo sullo stesso periodo costa minuti per ottenere
 * numeri che sono già in archivio. Ma la chiave di riconoscimento non guarda le
 * date: un backtest «trovato» può essere su tutt'altra finestra, e allora
 * aprirlo non risponde alla domanda. Le due cose vanno distinte.
 */
function BacktestGiaInArchivio({
  correlazione: c,
  stato,
  copre,
}: {
  correlazione: Correlazione | undefined;
  stato: StatoDati;
  copre: boolean;
}) {
  if (stato !== 'pieno' || !c || c.stato !== 'trovato' || !c.riga) return null;
  const r = c.riga;
  const p = etichettaPeriodo(r.startDate, r.endDate);

  return (
    <Avvertenza livello="nota">
      <b>Questa configurazione è già stata misurata:</b> backtest{' '}
      <Link to={`/backtest/${r.id}`}>#{r.id}</Link>
      {r.backtestGroupId != null ? ` del gruppo #${r.backtestGroupId}` : ' (singolo)'} su {p}
      {c.candidati > 1 ? `, e altri ${num(c.candidati - 1)} con gli stessi parametri` : ''}.{' '}
      {copre
        ? 'Copre già il periodo che hai chiesto: aprirlo costa un istante, rifare il calcolo costa minuti.'
        : 'Il periodo che hai chiesto qui NON è coperto da quel backtest: per quella finestra il calcolo va rifatto.'}
    </Avvertenza>
  );
}

/** Le finestre rapide. La prima riporta alla proposta, che è quella del bot. */
function Pillole({
  copertura,
  proposta,
  propostaAl,
  onScegli,
  onProposta,
}: {
  copertura: { prima: string | null; ultima: string | null };
  proposta: string;
  propostaAl: string;
  onScegli: (dal: string, al: string) => void;
  onProposta: () => void;
}) {
  const fine = copertura.ultima ? new Date(copertura.ultima) : new Date();
  const primaDati = copertura.prima ? new Date(copertura.prima) : null;

  return (
    <div className="sb-bt-pillole">
      <button
        type="button"
        className="sb-bt-pillola"
        onClick={onProposta}
        title={`${proposta} → ${propostaAl}`}
      >
        Come il bot
      </button>
      {FINESTRE.map((f) => (
        <button
          key={f.chiave}
          type="button"
          className="sb-bt-pillola"
          disabled={f.mesi == null && !primaDati}
          onClick={() => {
            if (f.mesi == null) {
              if (!primaDati) return;
              onScegli(giorno(primaDati), giorno(fine));
              return;
            }
            const inizio = menoMesi(f.mesi, fine);
            const dal = primaDati && inizio < primaDati ? primaDati : inizio;
            onScegli(giorno(dal), giorno(fine));
          }}
        >
          {f.etichetta}
        </button>
      ))}
    </div>
  );
}
